import { ResourceTimelineStatusDto, ResourceTimelineStatusDtoStatusEnum } from '@jooxter/api';
import { IQuickTimeSlot, DataPeriodEnum, fromISO, DateTimeToLocalTime } from '@jooxter/utils';
import { IGetResourceSlots, TSortSlotsReturn } from './types';

export const shouldReturnDefaultSlot = (
  timeline: ResourceTimelineStatusDto[],
  defaultSlot: IQuickTimeSlot,
  timezone: string
): boolean => {
  if (defaultSlot.period === DataPeriodEnum.H24 || defaultSlot.period === DataPeriodEnum.H48) {
    return true;
  }
  const hasOnlyOneSlot = timeline.length === 1;

  if (!hasOnlyOneSlot) {
    return false;
  }

  const timelineSlot = timeline[0];

  if (timelineSlot.status !== ResourceTimelineStatusDtoStatusEnum.Free) {
    return false;
  }

  const hasSameStart = fromISO(timelineSlot.start, timezone).equals(defaultSlot.start);
  const hasSameEnd = fromISO(timelineSlot.end, timezone).equals(defaultSlot.end);

  return hasSameStart && hasSameEnd;
};

export const extractFreeSlots = (slots: ResourceTimelineStatusDto[], timezone: string): IQuickTimeSlot[] => {
  return (
    slots
      ?.filter((timeslot) => timeslot.status === ResourceTimelineStatusDtoStatusEnum.Free)
      .map((value) => {
        const start = fromISO(value.start, timezone);
        const end = fromISO(value.end, timezone);
        const startTime = DateTimeToLocalTime(start);
        const endTime = DateTimeToLocalTime(end);

        return {
          text: `${startTime} - ${endTime}`,
          generatedByDefault: false,
          start,
          end,
        };
      }) ?? []
  );
};

export const sortSlots = (defaultSlots: IQuickTimeSlot[]): TSortSlotsReturn => {
  const result: TSortSlotsReturn = {
    dayDefaultSlot: undefined,
    amDefaultSlot: undefined,
    pmDefaultSlot: undefined,
  };

  defaultSlots.forEach((slot) => {
    switch (slot.period) {
      case DataPeriodEnum.Day:
        result.dayDefaultSlot = slot;
        break;
      case DataPeriodEnum.Am:
        result.amDefaultSlot = slot;
        break;
      case DataPeriodEnum.Pm:
        result.pmDefaultSlot = slot;
        break;
    }
  });

  return result;
};

export const getResourceSlots = ({ daySlots, amSlots, pmSlots }: IGetResourceSlots) => {
  const { dayDefaultSlot, resourceDaySlots } = daySlots;
  const { amDefaultSlot, resourceAmSlots } = amSlots;
  const { pmDefaultSlot, resourcePmSlots } = pmSlots;

  if (dayDefaultSlot) {
    if (resourceDaySlots.length === 1 && resourceDaySlots[0] === dayDefaultSlot) {
      return [dayDefaultSlot, amDefaultSlot, pmDefaultSlot].filter((s): s is IQuickTimeSlot => !!s);
    }

    return [...resourceAmSlots, ...resourcePmSlots];
  }

  if (pmDefaultSlot) {
    return resourcePmSlots;
  }

  return [];
};
